/**
 * Minimal i18n for voice-coding surfaces only.
 *
 * Scope deliberately tight: button labels, help-modal section
 * headings, AI toasts, and tooltip text shown around the voice
 * scoring panel. Voice commands themselves stay English — Whisper is
 * tuned on English cricket vocabulary, and the operator's spoken
 * vocabulary is hand-curated in useVoiceScoring.js.
 *
 * Why not react-i18next: full library overhead is unjustified for a
 * 40-string surface. This file is a flat dict + a 3-line lookup. If
 * we ever expand i18n to the rest of the app, swap this for the real
 * thing — call sites use a plain `t(key)` function which is forward-
 * compatible.
 *
 * Hindi translations are FIRST PASS — should be reviewed by a
 * Hindi-speaking team member before going to non-English operators.
 * Cricket-specific terms (e.g. "yorker", "wagon wheel") are kept as
 * English transliterations since they're loanwords in Indian cricket
 * commentary.
 */
import { useEffect, useState } from "react";


const STORAGE_KEY = "zcd_voice_lang";


const STRINGS = {
  en: {
    "voice.hold_to_talk":        "Hold to talk",
    "voice.recording":           "Recording",
    "voice.parsing":             "Parsing",
    "voice.voice":               "Voice",
    "voice.hands_free":          "Hands-free",
    "voice.tutorial":            "Tutorial",
    "voice.help":                "Help",
    "voice.calibrate":           "Calibrate",
    "voice.ai_fallback":         "AI fallback",
    "voice.noisy":               "Noisy",
    "voice.noisy_tooltip":       "Background noise is high — STT accuracy will degrade. Move closer to the mic or use a headset.",
    "voice.repeat_last":         "Repeat last",
    "voice.help.title":          "Voice scoring — cheat sheet",
    "voice.help.basics":         "Basics",
    "voice.help.balls":          "Ball outcomes",
    "voice.help.navigation":     "Navigation",
    "voice.help.spatial":        "Pitch / Impact / Wagon",
    "voice.help.cross_ball":     "Edit past balls",
    "voice.help.privacy":        "Privacy",
    "voice.ai_suggest_ok":       "AI suggested {n} fields (confidence: {c}). Tap chips to override.",
    "voice.ai_suggest_fail":     "AI suggest failed",
    "voice.charged":             "{n} Zencredits charged.",
    "voice.lang.label":          "Lang",
    "voice.lang.en":             "English",
    "voice.lang.hi":             "हिन्दी",
    "voice.tip.scoped":          "Tip: say \"pitch map\" first, then a cell number (e.g. \"23\") to pick a cell without it becoming runs.",
    "voice.tip.undo":            "Tip: say \"undo\" to remove the last saved ball, or \"undo ball 2.4\" for a specific past ball.",
    "voice.error.mic_permission": "Mic permission needed. Click the mic icon in your browser address bar → Allow.",
    "voice.error.no_audio":      "No audio captured — check your mic is plugged in and selected.",
  },
  hi: {
    // Cricket loanwords kept in English: "voice", "pitch", "wagon",
    // "yorker", "wide", "no ball", "boundary", "stroke", "STT", "AI".
    "voice.hold_to_talk":        "बोलने के लिए दबाएं",
    "voice.recording":           "रिकॉर्ड हो रहा है",
    "voice.parsing":             "समझ रहे हैं",
    "voice.voice":               "वॉइस",
    "voice.hands_free":          "हैंड्स-फ्री",
    "voice.tutorial":            "ट्यूटोरियल",
    "voice.help":                "मदद",
    "voice.calibrate":           "कैलिब्रेट",
    "voice.ai_fallback":         "AI सहायक",
    "voice.noisy":               "शोर",
    "voice.noisy_tooltip":       "बहुत शोर है — पहचान कम सटीक होगी। माइक के पास जाएं या हेडसेट इस्तेमाल करें।",
    "voice.repeat_last":         "फिर से",
    "voice.help.title":          "वॉइस स्कोरिंग — चीट शीट",
    "voice.help.basics":         "मूल बातें",
    "voice.help.balls":          "बॉल के नतीजे",
    "voice.help.navigation":     "नेविगेशन",
    "voice.help.spatial":        "पिच / इम्पैक्ट / वैगन",
    "voice.help.cross_ball":     "पिछली बॉल बदलें",
    "voice.help.privacy":        "प्राइवेसी",
    "voice.ai_suggest_ok":       "AI ने {n} फ़ील्ड सुझाए (विश्वास: {c}). बदलने के लिए चिप पर क्लिक करें।",
    "voice.ai_suggest_fail":     "AI सुझाव असफल",
    "voice.charged":             "{n} ज़ेनक्रेडिट कटे।",
    "voice.lang.label":          "भाषा",
    "voice.lang.en":             "English",
    "voice.lang.hi":             "हिन्दी",
    "voice.tip.scoped":          "टिप: पहले \"pitch map\" कहें, फिर सेल नंबर (जैसे \"23\") — रन नहीं गिनेगा।",
    "voice.tip.undo":            "टिप: \"undo\" से पिछली बॉल हटाएं, \"undo ball 2.4\" से कोई भी पुरानी बॉल।",
    "voice.error.mic_permission": "माइक की अनुमति चाहिए। ब्राउज़र के एड्रेस बार में माइक आइकन पर क्लिक करें → Allow.",
    "voice.error.no_audio":      "कोई ऑडियो नहीं — माइक जुड़ा है और चुना हुआ है यह देखें।",
  },
};


function getLang() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "en" || stored === "hi") return stored;
  } catch { /* SSR / privacy mode */ }
  return "en";
}


// Substitutes {placeholder} tokens with values from the provided map.
// Missing keys leave the placeholder in place so the bug is visible
// rather than silent.
function format(template, values) {
  if (!values) return template;
  return template.replace(/\{(\w+)\}/g, (_, key) =>
    Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : `{${key}}`,
  );
}


export function useVoiceI18n() {
  const [lang, setLangState] = useState(getLang);
  useEffect(() => {
    // Reload language if changed elsewhere (e.g. another tab). Cheap
    // poll over storage event; runs only while this hook is mounted.
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && (e.newValue === "en" || e.newValue === "hi")) {
        setLangState(e.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setLang = (next) => {
    if (next !== "en" && next !== "hi") return;
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    setLangState(next);
  };

  const t = (key, values) => {
    const dict = STRINGS[lang] || STRINGS.en;
    const raw = dict[key] ?? STRINGS.en[key] ?? key;
    return format(raw, values);
  };

  return { lang, setLang, t };
}
